import React, { useState, useMemo, useCallback } from 'react';
import type { Department, StaffMember, Shift, Schedule, View, AdminUser, AssignmentStatus, AttendanceRecord, AttendanceRequest, DayOfWeek, AppPage, ScheduleEntry, WeeklyMetrics, DailyMetrics, WorkloadFactors } from '../../types';
import ScheduleCell from './ScheduleCell';
import ShiftPalette from './ShiftPalette';
import RepeatScheduleModal from './RepeatScheduleModal';
import PrintOptionsModal from './PrintOptionsModal';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../layout/LoadingSpinner';
import { Icons } from '../ui/Icons';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange } from '../../utils/dateUtils';
import { PrintData } from './PrintPage';
import { DAY_INDEX_MAP } from '../../types';

import StaffProfileModal from '../staff/StaffProfileModal';
import ImportScheduleModal from './ImportScheduleModal';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

interface SchedulerViewProps {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weekStartDay: DayOfWeek;
    setSchedule: React.Dispatch<React.SetStateAction<Schedule>>;
    setView: (view: View) => void;
    onScheduleGenerated: (weekStartDate: Date) => void;
    onCellUpdate: (staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean, departmentId?: string | null, customUpdates?: Partial<ScheduleEntry>) => void;
    onRepeatSchedule: (sourceWeekStartDate: Date, repeatStartDate: Date, repeatEndDate: Date) => void;
    onInitiatePrint: (data: PrintData) => void;
    weeklyMetrics: WeeklyMetrics;
    onMetricsUpdate: (date: string, field: keyof DailyMetrics, value: number) => void;
    onImportSchedule?: (newSchedule: Schedule, weekStartDate: Date) => void;
    workloadFactors: WorkloadFactors;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
    allowedPages?: AppPage[];
    attendanceRecords?: AttendanceRecord[];
    attendanceRequests?: AttendanceRequest[];
    onUpdateAttendance?: (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, staffNote?: string, adminNote?: string }) => void;
    onCreateAttendanceRequest?: (staffId: string, date: string, updates: { requestedStartTime: string, requestedEndTime: string, staffNote?: string }) => void;
    showMetrics?: boolean;
}

const dayHeaderStyles = [
    'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',       // Monday
    'bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300', // Tuesday
    'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',   // Wednesday
    'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300',   // Thursday
    'bg-teal-50 text-teal-800 dark:bg-teal-900/20 dark:text-teal-300',     // Friday
    'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',     // Saturday
    'bg-indigo-50 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300', // Sunday
];

const dayHeaderBorderStyles = [
    'border-t-4 border-red-200 dark:border-red-700',
    'border-t-4 border-orange-200 dark:border-orange-700',
    'border-t-4 border-amber-200 dark:border-amber-700',
    'border-t-4 border-green-200 dark:border-green-700',
    'border-t-4 border-teal-200 dark:border-teal-700',
    'border-t-4 border-blue-200 dark:border-blue-700',
    'border-t-4 border-indigo-200 dark:border-indigo-700',
];

const WeekNavigator: React.FC<{
    currentWeekStartDate: Date;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    onRepeat: () => void;
    onPrint: () => void;
    onImport?: () => void;
    onWorkforce: () => void;
    totalWeeklyHours: number;
    showWorkforce?: boolean;
}> = ({ currentWeekStartDate, onPrevious, onNext, onToday, onRepeat, onPrint, onImport, onWorkforce, totalWeeklyHours, showWorkforce }) => (
    <div className="flex items-center justify-between mb-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-3 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
             <div className="flex items-center gap-1 bg-white dark:bg-slate-700/50 p-1 rounded-lg border border-gray-200 dark:border-slate-700">
                <button onClick={onPrevious} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300"><Icons.ChevronLeft className="h-5 w-5" /></button>
                <button onClick={onNext} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300"><Icons.ChevronRight className="h-5 w-5" /></button>
            </div>
            <button onClick={onToday} className="px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">Today</button>
            <button onClick={onRepeat} className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                <Icons.Copy className="w-4 h-4" />
                Repeat
            </button>
            <button onClick={onPrint} className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                <Icons.Printer className="w-4 h-4" />
                Print
            </button>
            <button onClick={onImport} className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                <Icons.Upload className="w-4 h-4" />
                Import
            </button>
            {showWorkforce && (
                <button onClick={onWorkforce} className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium border bg-white dark:bg-slate-700/50 border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">
                    <Icons.Search className="w-4 h-4" />
                    Workforce
                </button>
            )}
        </div>
        <div className="flex items-center gap-4 text-right">
            <h2 className="text-base font-bold text-gray-800 dark:text-gray-200">
                {formatDateRange(currentWeekStartDate)}
            </h2>
            <div className="w-px h-8 bg-gray-200 dark:bg-slate-700"></div>
            <div className="px-2 py-1 rounded-lg bg-white/50 dark:bg-slate-700/50">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Total Hours</p>
                <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {(totalWeeklyHours ?? 0).toFixed(1)}
                </p>
            </div>
        </div>
    </div>
);

const SchedulerView: React.FC<SchedulerViewProps> = ({ 
    departments, 
    people, 
    shifts, 
    schedule, 
    weekStartDay, 
    setSchedule, 
    setView, 
    onScheduleGenerated, 
    onCellUpdate, 
    onRepeatSchedule, 
    onInitiatePrint,
    weeklyMetrics,
    onMetricsUpdate,
    onImportSchedule,
    workloadFactors,
    isAdmin, 
    isSuperAdmin, 
    allowedPages, 
    attendanceRecords = [], 
    attendanceRequests = [],
    onUpdateAttendance,
    onCreateAttendanceRequest,
    showMetrics = true
}) => {
    const [draggedShiftId, setDraggedShiftId] = useState<string | null>(null);
    const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    const startDayIndex = DAY_INDEX_MAP[weekStartDay];
    const [currentWeekStartDate, setCurrentWeekStartDate] = useState(() => getWeekStartDate(new Date(), startDayIndex));

    // Update week start when settings change
    React.useEffect(() => {
        setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));
    }, [startDayIndex]);
    const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
    const [selectedStaffForProfile, setSelectedStaffForProfile] = useState<SchedulablePerson | null>(null);

    const weekDates = useMemo(() => getWeekDates(currentWeekStartDate), [currentWeekStartDate]);

    const goToPreviousWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() - 7);
        return newDate;
    });

    const goToNextWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + 7);
        return newDate;
    });
    
    const goToToday = () => setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));

    const handleUpdateSchedule = useCallback((staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean, departmentId?: string | null, customUpdates?: Partial<ScheduleEntry>) => {
        onCellUpdate(staffId, date, shiftId, status, isForced, departmentId, customUpdates);
    }, [onCellUpdate]);

    const handleRepeat = (repeatStartDate: Date, repeatEndDate: Date) => {
        onRepeatSchedule(currentWeekStartDate, repeatStartDate, repeatEndDate);
        setIsRepeatModalOpen(false);
    };

    const handlePrint = (departmentId: string) => {
        onInitiatePrint({
            departments,
            people,
            shifts,
            schedule,
            weekStartDate: currentWeekStartDate,
            departmentId,
        });
        setIsPrintModalOpen(false);
    };

    const handleImport = (newSchedule: Schedule, weekStartDate: Date) => {
        if (onImportSchedule) {
            onImportSchedule(newSchedule, weekStartDate);
        } else {
            setSchedule(prev => {
                const updated = { ...prev };
                Object.keys(newSchedule).forEach(staffId => {
                    if (!updated[staffId]) updated[staffId] = {};
                    Object.assign(updated[staffId], newSchedule[staffId]);
                });
                return updated;
            });
        }
        setCurrentWeekStartDate(weekStartDate);
        setIsImportModalOpen(false);
    };

    console.log("SchedulerView rendering with:", { departmentsCount: departments.length, peopleCount: people.length, shiftsCount: shifts.length });
    const departmentStaffMap = useMemo(() => {
        const map: { [depId: string]: SchedulablePerson[] } = {};
        departments.forEach(dep => {
            map[dep.id] = [];
        });

        people.forEach(person => {
            // Normalize departmentIds to an array of strings
            let deptIds: string[] = [];
            const p = person as any;
            if (Array.isArray(p.departmentIds)) {
                deptIds = p.departmentIds;
            } else if (typeof p.departmentIds === 'string') {
                deptIds = [p.departmentIds];
            } else if (p.departmentId) {
                deptIds = [p.departmentId];
            }

            if (deptIds.length > 0) {
                // Use primary department for initial mapping
                const primaryDepId = deptIds[0];
                if (map[primaryDepId]) {
                    map[primaryDepId].push(person);
                } else {
                    // Fallback: try other departments if primary doesn't match
                    for (const id of deptIds) {
                        if (map[id]) {
                            map[id].push(person);
                            break;
                        }
                    }
                }
            }
        });
        return map;
    }, [departments, people]);

    const dailyStaffCount = useMemo(() => {
        const counts: { [date: string]: { [depId: string]: number } } = {};
        
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            counts[dateString] = {};
            departments.forEach(dep => {
                counts[dateString][dep.id] = 0;
            });
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule) {
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED') {
                        // Grouping logic: prioritize the departmentId on the entry itself
                        const p = person as any;
                        const deptIds = Array.isArray(p.departmentIds) ? p.departmentIds : (typeof p.departmentIds === 'string' ? [p.departmentIds] : (p.departmentId ? [p.departmentId] : []));
                        const depId = entry.departmentId || deptIds.find(id => departments.some(d => d.id === id)) || deptIds[0];
                        
                        if (depId && counts[dateString] && typeof counts[dateString][depId] === 'number') {
                            counts[dateString][depId]++;
                        }
                    }
                });
            }
        });

        return counts;
    }, [schedule, people, departments, weekDates]);

    const staffWeeklyHours = useMemo(() => {
        const hours: { [staffId: string]: number } = {};
        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));
        for (const person of people) {
            let personHours = 0;
            for (const date of weekDates) {
                const dateString = formatDateYYYYMMDD(date);
                const entry = schedule[person.id]?.[dateString];
                
                if (entry?.status === 'ASSIGNED') {
                    let startStr = entry.customStartTime;
                    let endStr = entry.customEndTime;

                    if (!startStr || !endStr) {
                        const shift = entry.shiftId ? shiftsMap.get(entry.shiftId) : null;
                        if (shift) {
                            startStr = startStr || shift.startTime;
                            endStr = endStr || shift.endTime;
                        }
                    }

                    if (startStr && endStr) {
                        const start = new Date(`1970-01-01T${startStr}:00`);
                        const end = new Date(`1970-01-01T${endStr}:00`);
                        if (end < start) { // Handles overnight shifts
                            end.setDate(end.getDate() + 1);
                        }
                        personHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }
                }
            }
            hours[person.id] = personHours;
        }
        return hours;
    }, [schedule, people, shifts, weekDates]);

    const departmentHours = useMemo(() => {
        const daily: { [date: string]: { [depId: string]: number } } = {};
        const weekly: { [depId: string]: number } = {};
        
        // Initialize weekly totals for all departments
        departments.forEach(dep => {
            weekly[dep.id] = 0;
        });

        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            daily[dateString] = {};
            departments.forEach(dep => {
                daily[dateString][dep.id] = 0;
            });
        });

        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule) {
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    
                    if (entry && entry.status === 'ASSIGNED') {
                        let startStr = entry.customStartTime;
                        let endStr = entry.customEndTime;
                        
                        if (!startStr || !endStr) {
                            const shift = entry.shiftId ? shiftsMap.get(entry.shiftId) : null;
                            if (shift) {
                                startStr = startStr || shift.startTime;
                                endStr = endStr || shift.endTime;
                            }
                        }

                        if (startStr && endStr) {
                            // Grouping logic: prioritize the departmentId on the entry itself
                            const p = person as any;
                            const deptIds = Array.isArray(p.departmentIds) ? p.departmentIds : (typeof p.departmentIds === 'string' ? [p.departmentIds] : (p.departmentId ? [p.departmentId] : []));
                            const depId = entry.departmentId || deptIds.find(id => departments.some(d => d.id === id)) || deptIds[0];

                            if (depId && daily[dateString] && typeof daily[dateString][depId] === 'number') {
                                const start = new Date(`1970-01-01T${startStr}:00`);
                                const end = new Date(`1970-01-01T${endStr}:00`);
                                if (end < start) {
                                    end.setDate(end.getDate() + 1);
                                }
                                const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                                daily[dateString][depId] += duration;
                                weekly[depId] += duration;
                            }
                        }
                    }
                });
            }
        });

        return { daily, weekly };
    }, [schedule, people, shifts, weekDates, departments]);

    const dailyTotalHours = useMemo(() => {
        const daily: { [date: string]: number } = {};
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            const hoursPerDep = departmentHours.daily[dateString] || {};
            daily[dateString] = Object.values(hoursPerDep).reduce((sum: number, h: number) => sum + h, 0);
        });
        return daily;
    }, [departmentHours, weekDates]);

    const calculateWorkloadRatio = useCallback((dateStr: string) => {
        const metrics = weeklyMetrics[dateStr] || { arrivals: 0, departures: 0, stayOvers: 0 };
        
        // Find Front Desk department
        const frontDeskDep = departments.find(d => d.name.toLowerCase() === 'front desk');
        const frontDeskHours = frontDeskDep ? (departmentHours.daily[dateStr]?.[frontDeskDep.id] || 0) : 0;
        
        if (frontDeskHours === 0) return null;

        const totalMinutes = 
            ((metrics.arrivals || 0) * (workloadFactors?.arrivalTime || 6)) + 
            ((metrics.departures || 0) * (workloadFactors?.departureTime || 3)) + 
            ((metrics.stayOvers || 0) * (workloadFactors?.stayoverTime || 1));
        
        const workloadHours = (totalMinutes / 60) * (workloadFactors?.bufferMultiplier || 1.2);
        const adjustedWorkloadHours = workloadHours * (1 + (workloadFactors?.workloadFactor || 0));
        const ratio = adjustedWorkloadHours / frontDeskHours;
        
        let label = 'Optimal';
        let colorClass = 'text-green-600 dark:text-green-400';
        let bgClass = 'bg-green-100 dark:bg-green-900/30';
        
        if (ratio < 0.8) {
            label = 'Overstaffed';
            colorClass = 'text-blue-600 dark:text-blue-400';
            bgClass = 'bg-blue-100 dark:bg-blue-900/30';
        } else if (ratio > 1.2) {
            label = 'Understaffed';
            colorClass = 'text-red-600 dark:text-red-400';
            bgClass = 'bg-red-100 dark:bg-red-900/30';
        } else if (ratio > 1.0) {
            label = 'Busy';
            colorClass = 'text-amber-600 dark:text-amber-400';
            bgClass = 'bg-amber-100 dark:bg-amber-900/30';
        }

        return { ratio, label, colorClass, bgClass, frontDeskHours, workloadHours, adjustedWorkloadHours };
    }, [weeklyMetrics, departmentHours, workloadFactors, departments]);

    const totalWeeklyHours = useMemo(() => {
        // FIX: Explicitly type the parameters of the 'reduce' function to resolve a TypeScript inference issue where the '+' operator was being applied to 'unknown' types. This ensures 'sum' and 'hours' are correctly treated as numbers.
        return Object.values(staffWeeklyHours).reduce((sum: number, hours: number) => sum + hours, 0);
    }, [staffWeeklyHours]);

    const selectedStaffDepartment = useMemo(() => {
        if (!selectedStaffForProfile?.departmentIds || selectedStaffForProfile.departmentIds.length === 0) return undefined;
        return departments.find(d => d.id === selectedStaffForProfile.departmentIds[0]);
    }, [selectedStaffForProfile, departments]);


    return (
        <div className="flex flex-col lg:flex-row gap-6">
             {isRepeatModalOpen && (
                <RepeatScheduleModal
                    sourceWeekStart={currentWeekStartDate}
                    onClose={() => setIsRepeatModalOpen(false)}
                    onRepeat={handleRepeat}
                />
             )}
             {isPrintModalOpen && (
                <PrintOptionsModal
                    departments={departments}
                    onClose={() => setIsPrintModalOpen(false)}
                    onPrint={handlePrint}
                />
             )}
             {isImportModalOpen && (
                <ImportScheduleModal
                    people={people}
                    shifts={shifts}
                    onClose={() => setIsImportModalOpen(false)}
                    onImport={handleImport}
                />
             )}
             {selectedStaffForProfile && (
                <StaffProfileModal
                    staff={selectedStaffForProfile as StaffMember}
                    departments={departments}
                    onClose={() => setSelectedStaffForProfile(null)}
                />
             )}
            <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isPaletteCollapsed ? 'lg:w-20' : 'lg:w-1/4 xl:w-1/5'}`}>
                <ShiftPalette 
                    shifts={shifts} 
                    setDraggedShiftId={setDraggedShiftId} 
                    isCollapsed={isPaletteCollapsed}
                    onToggleCollapse={() => setIsPaletteCollapsed(prev => !prev)}
                />
            </div>

            <div className="flex-1 overflow-x-auto">
                 <WeekNavigator 
                    currentWeekStartDate={currentWeekStartDate}
                    onPrevious={goToPreviousWeek}
                    onNext={goToNextWeek}
                    onToday={goToToday}
                    onRepeat={() => setIsRepeatModalOpen(true)}
                    onPrint={() => setIsPrintModalOpen(true)}
                    onImport={() => setIsImportModalOpen(true)}
                    onWorkforce={() => setView({ page: 'workforce' })}
                    totalWeeklyHours={totalWeeklyHours}
                    showWorkforce={allowedPages?.includes('workforce')}
                />
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-md overflow-x-auto">
                    <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-y-auto max-h-[calc(100vh-200px)]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 table-fixed">
                            <thead className="bg-gray-50 dark:bg-slate-900/50 sticky top-0 z-30 shadow-sm">
                                <tr>
                                    <th scope="col" className="sticky left-0 bg-gray-50 dark:bg-slate-900/50 z-40 px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48 border-r border-gray-200 dark:border-slate-700">
                                        Staff Member
                                    </th>
                                    {weekDates.map((date, index) => {
                                        const { dayLong, date: dateNum } = formatDayAndDate(date);
                                        return (
                                            <th key={date.toISOString()} scope="col" className={`px-2 py-2 text-center w-32 ${dayHeaderStyles[index]} ${dayHeaderBorderStyles[index]} bg-gray-50 dark:bg-slate-900/50 z-30`}>
                                                <div className="text-xl font-bold">{dateNum}</div>
                                                <div className="text-[10px] font-semibold uppercase tracking-wider">{dayLong}</div>
                                            </th>
                                        )
                                    })}
                                    <th scope="col" className="sticky right-0 bg-gray-50 dark:bg-slate-900/50 z-40 px-4 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 border-l border-gray-200 dark:border-slate-700">
                                        Hours
                                    </th>
                                </tr>
                                 {/* Daily Metrics Section - Moved to thead for sticky behavior */}
                                {showMetrics && (
                                    <>
                                        <tr className="bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-gray-200 dark:border-slate-700">
                                            <td className="sticky left-0 bg-emerald-50/90 dark:bg-emerald-900/90 z-20 px-4 py-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                Arrivals
                                            </td>
                                            {weekDates.map(date => {
                                                const dateStr = formatDateYYYYMMDD(date);
                                                const val = weeklyMetrics[dateStr]?.arrivals || 0;
                                                return (
                                                    <td key={dateStr} className="px-2 py-1 text-center">
                                                        <div className="flex flex-row items-center justify-center gap-2">
                                                            <input 
                                                                type="number"
                                                                value={val || ''}
                                                                onChange={(e) => onMetricsUpdate(dateStr, 'arrivals', parseInt(e.target.value) || 0)}
                                                                className="w-16 px-1 py-0.5 text-center text-xs font-bold border border-emerald-200 dark:border-emerald-800 rounded-md outline-none focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-400 shadow-sm"
                                                                placeholder="0"
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="sticky right-0 bg-emerald-50/90 dark:bg-emerald-900/90 z-20 border-l border-gray-200 dark:border-slate-700"></td>
                                        </tr>
                                        <tr className="bg-rose-50/50 dark:bg-rose-900/10 border-b border-gray-200 dark:border-slate-700">
                                            <td className="sticky left-0 bg-rose-50/90 dark:bg-rose-900/90 z-20 px-4 py-1 text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                Departures
                                            </td>
                                            {weekDates.map(date => {
                                                const dateStr = formatDateYYYYMMDD(date);
                                                const val = weeklyMetrics[dateStr]?.departures || 0;
                                                return (
                                                    <td key={dateStr} className="px-2 py-1 text-center">
                                                        <div className="flex flex-row items-center justify-center gap-2">
                                                            <input 
                                                                type="number"
                                                                value={val || ''}
                                                                onChange={(e) => onMetricsUpdate(dateStr, 'departures', parseInt(e.target.value) || 0)}
                                                                className="w-16 px-1 py-0.5 text-center text-xs font-bold border border-rose-200 dark:border-rose-800 rounded-md outline-none focus:ring-1 focus:ring-rose-500 bg-white dark:bg-slate-800 text-rose-700 dark:text-rose-400 shadow-sm"
                                                                placeholder="0"
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="sticky right-0 bg-rose-50/90 dark:bg-rose-900/90 z-20 border-l border-gray-200 dark:border-slate-700"></td>
                                        </tr>
                                        <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b border-gray-200 dark:border-slate-700">
                                            <td className="sticky left-0 bg-blue-50/90 dark:bg-blue-900/90 z-20 px-4 py-1 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                Stay-over
                                            </td>
                                            {weekDates.map(date => {
                                                const dateStr = formatDateYYYYMMDD(date);
                                                const val = weeklyMetrics[dateStr]?.stayOvers || 0;
                                                return (
                                                    <td key={dateStr} className="px-2 py-1 text-center">
                                                        <div className="flex flex-row items-center justify-center gap-2">
                                                            <input 
                                                                type="number"
                                                                value={val || ''}
                                                                onChange={(e) => onMetricsUpdate(dateStr, 'stayOvers', parseInt(e.target.value) || 0)}
                                                                className="w-16 px-1 py-0.5 text-center text-xs font-bold border border-blue-200 dark:border-blue-800 rounded-md outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-400 shadow-sm"
                                                                placeholder="0"
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="sticky right-0 bg-blue-50/90 dark:bg-blue-900/90 z-20 border-l border-gray-200 dark:border-slate-700"></td>
                                        </tr>
                                        <tr className="bg-indigo-50/50 dark:bg-indigo-900/10 border-b border-gray-200 dark:border-slate-700">
                                            <td className="sticky left-0 bg-indigo-50/90 dark:bg-indigo-900/90 z-20 px-4 py-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                <div className="flex flex-col">
                                                    <span>Workload Ratio</span>
                                                    <span className="text-[8px] font-medium normal-case opacity-70">Front Desk Only</span>
                                                </div>
                                            </td>
                                            {weekDates.map(date => {
                                                const dateStr = formatDateYYYYMMDD(date);
                                                const workload = calculateWorkloadRatio(dateStr);
                                                if (!workload) return <td key={dateStr} className="px-2 py-1 text-center text-[10px] text-gray-400 italic">No FD Staff</td>;
                                                return (
                                                    <td key={dateStr} className="px-2 py-1 text-center">
                                                        <div className="flex flex-col items-center justify-center group relative">
                                                            <div className={`px-2 py-0.5 rounded-full text-[11px] font-black shadow-sm ${workload.bgClass} ${workload.colorClass} border border-current/20`}>
                                                                {workload.ratio.toFixed(2)}
                                                            </div>
                                                            <span className={`text-[9px] font-bold uppercase tracking-tight mt-0.5 ${workload.colorClass}`}>{workload.label}</span>
                                                            
                                                            {/* Tooltip */}
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-xl">
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span>Base WL: {workload.workloadHours.toFixed(1)}h</span>
                                                                    <span className="text-indigo-300 font-bold">Adj WL: {workload.adjustedWorkloadHours.toFixed(1)}h</span>
                                                                    <span className="border-t border-slate-700 mt-0.5 pt-0.5">FD Capacity: {workload.frontDeskHours.toFixed(1)}h</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="sticky right-0 bg-indigo-50/90 dark:bg-indigo-900/90 z-20 border-l border-gray-200 dark:border-slate-700"></td>
                                        </tr>
                                    </>
                                )}
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                {departments.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                            <div className="flex flex-col items-center gap-2">
                                                <Icons.AlertTriangle className="w-8 h-8 text-gray-400" />
                                                <p className="text-lg font-medium">No departments found.</p>
                                                <p className="text-sm italic">Please add departments in the Settings to start scheduling.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : departments.map(dep => (
                                    <React.Fragment key={dep.id}>
                                        <tr className="bg-gray-200 dark:bg-slate-700 sticky top-[57px] z-20 shadow-sm">
                                            <td className="sticky left-0 bg-gray-200 dark:bg-slate-700 z-30 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 w-48 border-r border-gray-300 dark:border-slate-600" style={{ color: dep.color }}>
                                                {dep.name}
                                            </td>
                                            {weekDates.map(date => {
                                                const dateString = formatDateYYYYMMDD(date);
                                                return (
                                                    <td key={dateString} className="px-3 py-2 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 z-20">
                                                        <div className="flex flex-col items-center">
                                                            <span>{dailyStaffCount[dateString]?.[dep.id] || 0} Staff</span>
                                                            <span className="text-[10px] opacity-70">{(departmentHours.daily[dateString]?.[dep.id] || 0).toFixed(1)} hrs</span>
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                            <td className="sticky right-0 bg-gray-200 dark:bg-slate-700 z-30 px-4 py-2 w-24 border-l border-gray-300 dark:border-slate-600 text-center text-sm font-bold text-indigo-700 dark:text-indigo-300">
                                                {(departmentHours.weekly[dep.id] || 0).toFixed(1)}
                                            </td>
                                        </tr>
                                        {(!departmentStaffMap[dep.id] || departmentStaffMap[dep.id].length === 0) ? (
                                            <tr>
                                                <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                                                    No staff assigned to this department.
                                                </td>
                                            </tr>
                                        ) : (
                                            departmentStaffMap[dep.id].map(person => {
                                                const p = person as any;
                                                const personDeptIds = Array.isArray(p.departmentIds) ? p.departmentIds : (typeof p.departmentIds === 'string' ? [p.departmentIds] : (p.departmentId ? [p.departmentId] : []));
                                                
                                                return (
                                                    <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 group">
                                                        <td className="sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-700/50 z-20 px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 w-48 border-r border-gray-200 dark:border-slate-700" style={{ borderLeft: `4px solid ${person.color}` }}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="truncate">
                                                                    {person.name}
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                                                        <span className="truncate max-w-[100px]">{person.role}</span>
                                                                        <button 
                                                                            onClick={() => setSelectedStaffForProfile(person)}
                                                                            className="p-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all shadow-sm border border-indigo-100 dark:border-indigo-800/50"
                                                                            title="View Staff Profile"
                                                                        >
                                                                            <Icons.ExternalLink className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {weekDates.map(date => {
                                                            const dateString = formatDateYYYYMMDD(date);
                                                            return (
                                                                <ScheduleCell
                                                                    key={`${person.id}-${dateString}`}
                                                                    staffId={person.id}
                                                                    date={dateString}
                                                                    entry={schedule[person.id]?.[dateString]}
                                                                    shifts={shifts}
                                                                    onUpdate={handleUpdateSchedule}
                                                                    draggedShiftId={draggedShiftId}
                                                                    personName={person.name}
                                                                    personAvailability={{
                                                                        availability: person.availability,
                                                                        availabilityOverrides: person.availabilityOverrides,
                                                                    }}
                                                                    staffDepartments={departments.filter(d => personDeptIds.includes(d.id))}
                                                                    allDepartments={departments}
                                                                    attendanceRecords={attendanceRecords}
                                                                    attendanceRequests={attendanceRequests}
                                                                    onUpdateAttendance={onUpdateAttendance}
                                                                    onCreateAttendanceRequest={onCreateAttendanceRequest}
                                                                    onViewDetails={(id) => {
                                                                        const person = people.find(p => p.id === id);
                                                                        if (person) setSelectedStaffForProfile(person);
                                                                    }}
                                                                    isAdmin={isAdmin}
                                                                    isSuperAdmin={isSuperAdmin}
                                                                />
                                                            )
                                                        })}
                                                        <td className="sticky right-0 bg-white dark:bg-slate-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-700/50 z-20 px-4 py-2 whitespace-nowrap text-center text-sm font-bold text-indigo-600 dark:text-indigo-400 w-24 border-l border-gray-200 dark:border-slate-700">
                                                            {(staffWeeklyHours[person.id] ?? 0).toFixed(1)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchedulerView;